import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { AuthContext } from "../../../../Header/auth - context";
import CustomDropdown from "../../../../custom/customDropdown";
import handleStampaDokumenta from '../../../../custom/dokumentStampa';

/* Datum ide kroz toUtcIso — bez korekcije offseta toISOString ume da pomeri dan
   unazad (vidi CLAUDE.md). */
const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + "Z";
};

const praznaForma = {
    id_zaposleni: null,
    tip_dokumenta: null,
    datum_dokumenta: new Date(),
    vazi_od: null,
    vazi_do: null,
    broj_polise: "",
    osiguravac: "",
    zavedeni_broj: "",
    napomena: "",
};

const StampaTab = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const toast = useRef(null);
    const [tipoviDB, setTipoviDB] = useState([]);
    const [zaposleniDB, setZaposleniDB] = useState([]);
    const [radiSe, setRadiSe] = useState(false);
    const [forma, setForma] = useState(praznaForma);
    const [formSubmitted, setFormSubmitted] = useState(false);

    /* Obavezna polja se crvene tek posle pokušaja slanja — isto kao u
       ZAPOSLENI > UNOS. Zato nema zvezdica uz labele. */
    const invalidControl = (vrednost) => formSubmitted && (vrednost === null || vrednost === undefined || vrednost === "");

    const izmeni = (polje, vrednost) => setForma((prev) => ({ ...prev, [polje]: vrednost }));

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 4000 });

    const zaglavlje = useCallback(() => ({
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
    }), [auth.token]);

    const posalji = useCallback(async (step, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/employees/stampaDokumentacije", {
            method: "POST",
            headers: zaglavlje(),
            body: JSON.stringify({ step, ...telo }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.message || t("stampaDok.greska"));
        return data;
    }, [zaglavlje, t]);

    const ucitaj = useCallback(async () => {
        try {
            const d = await posalji("INIT");
            setTipoviDB(d.TipoviDokumenata || []);
            setZaposleniDB(d.ZaposleniDB || []);
        } catch (e) {
            poruka("error", t("common.greska"), e.message);        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [posalji, t]);

    useEffect(() => { ucitaj(); }, [ucitaj]);

    /* Izabrani tip nosi podatak da li firma uopšte ima šablon za njega — dugme se
       gasi unapred umesto da korisnik sazna tek posle klika. */
    const izabraniTip = tipoviDB.find((x) => x.value === forma.tip_dokumenta);
    const nemaSablon = !!izabraniTip && !izabraniTip.imaSablon;

    /* PZO traži broj polise, osiguravača i period važenja; ostali tipovi ne. */
    const jePZO = /osiguran/i.test(String(izabraniTip?.label || ""));
    /* Sporazum o raskidu i Obrazac 6 koriste datume perioda pod drugim nazivima */
    const jeSporazum = /sporazum|prestank/i.test(String(izabraniTip?.label || ""));

    const odstampaj = async () => {
        setFormSubmitted(true);
        /* Bez toasta — prazna obavezna polja se već crvene kroz invalid. */
        if (!forma.id_zaposleni || !forma.tip_dokumenta) return;
        setRadiSe(true);
        try {
            const r = await posalji("PREGLED", {
                id_zaposleni: forma.id_zaposleni,
                formData: {
                    tip_dokumenta: forma.tip_dokumenta,
                    naziv_dokumenta: izabraniTip?.label || "",
                    datum_dokumenta: toUtcIso(forma.datum_dokumenta),
                    vazi_od: toUtcIso(forma.vazi_od),
                    vazi_do: toUtcIso(forma.vazi_do),
                    broj_polise: forma.broj_polise || null,
                    osiguravac: forma.osiguravac || null,
                    napomena: forma.napomena || null,
                    zavedeni_broj: forma.zavedeni_broj || null,
                },
            });

            /* Šablon za firmu ne postoji (ili zaposleni nije nađen) — isto
               ponašanje kao kod štampe ugovora: upozorenje, bez zavođenja. */
            if (r.message) {
                poruka("warn", t("stampaDok.naslov"), r.message);
                return;
            }

            handleStampaDokumenta(r);
            /* Forma se namerno NE prazni: dokument nije zaveden, pa isti
               podaci trebaju ponovo u tabu UNOS kad stigne potpisan primerak. */
            setFormSubmitted(false);
        } catch (e) {
            poruka("error", t("common.greska"), e.message);
        } finally {
            setRadiSe(false);
        }
    };

    return (
        <div className="col-12">
            <Toast ref={toast} />

            <div className="blagajnaDashboard dnevniceDashboard">
                <Card>
                    <div className="stampaDokForma">
                        <div className="sd-polje">
                            <label>{t("stampaDok.zaposleni")}:</label>
                            <CustomDropdown style={{ width: "100%" }} options={zaposleniDB}
                                value={forma.id_zaposleni} optionLabel="label"
                                required invalid={invalidControl(forma.id_zaposleni)}
                                placeholder={t("stampaDok.izaberiZaposlenog")}
                                onChange={(e) => izmeni("id_zaposleni", e.target.value)} />
                        </div>

                        <div className="sd-polje">
                            <label>{t("stampaDok.tipDokumenta")}:</label>
                            <CustomDropdown style={{ width: "100%" }} options={tipoviDB}
                                value={forma.tip_dokumenta} optionLabel="label"
                                required invalid={invalidControl(forma.tip_dokumenta)}
                                placeholder={t("stampaDok.izaberiTip")}
                                emptyMessage={t("stampaDok.nemaTipovaZaFirmu")}
                                onChange={(e) => izmeni("tip_dokumenta", e.target.value)} />
                        </div>

                        {/* Zavodni broj je opcion: ako ga upišeš, štampa se na
                            papiru; ako ne, ostaje crta za ručni upis. */}
                        <div className="sd-polje">
                            <label>{t("stampaDok.zavedeniBroj")}:</label>
                            <InputText value={forma.zavedeni_broj} style={{ width: "100%" }}
                                placeholder={t("stampaDok.zavedeniBrojPh")}
                                onChange={(e) => izmeni("zavedeni_broj", e.target.value)} />
                        </div>

                        <div className="sd-polje">
                            <label>{t("stampaDok.datumDokumenta")}:</label>
                            <Calendar value={forma.datum_dokumenta} onChange={(e) => izmeni("datum_dokumenta", e.value)}
                                dateFormat="dd.mm.yy" mask="99.99.9999" showIcon style={{ width: "100%" }} />
                        </div>

                        {/* PZO nosi polisu i period pokrića */}
                        {jePZO && (
                            <>
                                <div className="sd-polje">
                                    <label>{t("stampaDok.brojPolise")}:</label>
                                    <InputText value={forma.broj_polise} style={{ width: "100%" }}
                                        onChange={(e) => izmeni("broj_polise", e.target.value)} />
                                </div>
                                <div className="sd-polje">
                                    <label>{t("stampaDok.osiguravac")}:</label>
                                    <InputText value={forma.osiguravac} style={{ width: "100%" }}
                                        onChange={(e) => izmeni("osiguravac", e.target.value)} />
                                </div>
                            </>
                        )}

                        {/* Sporazum ne traži datum zasnivanja — on se vuče iz
                            Unos > Podaci o radnom odnosu, da papir ne bi mogao
                            da odstupi od kartona zaposlenog. */}
                        {!jeSporazum && (
                            <div className="sd-polje">
                                <label>{jePZO ? t("stampaDok.osiguranjeOd") : t("stampaDok.vaziOd")}:</label>
                                <Calendar value={forma.vazi_od} onChange={(e) => izmeni("vazi_od", e.value)}
                                    dateFormat="dd.mm.yy" mask="99.99.9999" showIcon style={{ width: "100%" }} />
                            </div>
                        )}
                        <div className="sd-polje">
                            <label>{jePZO ? t("stampaDok.osiguranjeDo")
                                : jeSporazum ? t("stampaDok.datumPrestanka") : t("stampaDok.vaziDo")}:</label>
                            <Calendar value={forma.vazi_do} onChange={(e) => izmeni("vazi_do", e.value)}
                                dateFormat="dd.mm.yy" mask="99.99.9999" showIcon style={{ width: "100%" }}
                                minDate={forma.vazi_od || undefined} />
                        </div>

                        {jeSporazum && (
                            <small className="sd-puno" style={{ color: "#6c757d" }}>
                                {t("stampaDok.sporazumNapomena")}
                            </small>
                        )}

                        <div className="sd-polje sd-puno">
                            <label>{t("stampaDok.napomena")}:</label>
                            <InputText value={forma.napomena} style={{ width: "100%" }}
                                onChange={(e) => izmeni("napomena", e.target.value)} />
                        </div>

                        {nemaSablon && (
                            <small className="sd-puno" style={{ color: "#d9534f" }}>
                                {t("stampaDok.nemaSablonaUpozorenje")}
                            </small>
                        )}

                        <div className="sd-puno sd-akcija">
                            <button type="button" onClick={odstampaj} disabled={radiSe}
                                className="btn btn-success">
                                {t("stampaDok.zavediIStampaj")}
                            </button>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default StampaTab;
